import { useEffect, useState, useCallback } from "react";
import { PipedreamClient } from "@pipedream/sdk/browser";
import { Plus, Loader2, AlertCircle, X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectSeparator,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useConnectionsStore } from "@/store/connectionsStore";
import { useAgentWorkflowAppsStore } from "@/store/agentWorkflowAppsStore";
import { fetchApi } from "@/lib/api";
import { subscribeToOAuthConnections } from "@/lib/oauthBroadcast";
import { DynamicsConnectionDialog } from "@/components/erp/DynamicsConnectionDialog";
import { EpicorConnectionDialog } from "@/components/erp/EpicorConnectionDialog";
import { Prophet21ConnectionDialog } from "@/components/erp/Prophet21ConnectionDialog";
import { JobBoss2ConnectionDialog } from "@/components/erp/JobBoss2ConnectionDialog";
import { OdysseyConnectionDialog } from "@/components/erp/OdysseyConnectionDialog";
import { EniteoConnectionDialog } from "@/components/erp/EniteoConnectionDialog";
import type { ConnectionProviderKey } from "@forgepoint/shared";
import {
  DYNAMICS_PROVIDER_ID,
  EPICOR_PROVIDER_ID,
  PROPHET21_PROVIDER_ID,
  JOBBOSS2_PROVIDER_ID,
  ODYSSEY_PROVIDER_ID,
  ENITEO_PROVIDER_ID,
  PIPEDREAM_PROVIDER_ID,
  OAUTH_PROVIDERS,
} from "@forgepoint/shared";
import { useFeatureFlag } from "@/store/featureFlagStore";

interface ConnectionSelectorProps {
  appId: string;
  providerId: ConnectionProviderKey;
  connectionId?: string | null;
  onSelect: (connectionId: string) => void;
  disabledConnectionIds?: string[];
  disabled?: boolean;
}

export function ConnectionSelector({
  appId,
  providerId,
  connectionId,
  onSelect,
  disabledConnectionIds = [],
  disabled = false,
}: ConnectionSelectorProps) {
  const epicorPrimitiveSupportEnabled = useFeatureFlag(
    "epicor-primitive-support",
  );
  const prophet21SupportEnabled = useFeatureFlag("prophet21-support");
  const jobBoss2Enabled = useFeatureFlag("jobboss2-erp-integration");
  const odysseyEnabled = useFeatureFlag("odyssey-erp-integration");
  const eniteoSupportEnabled = useFeatureFlag("eniteo-support");
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPipedreamActive, setIsPipedreamActive] = useState(false);
  const [isDynamicsDialogOpen, setIsDynamicsDialogOpen] = useState(false);
  const [isEpicorDialogOpen, setIsEpicorDialogOpen] = useState(false);
  const [isProphet21DialogOpen, setIsProphet21DialogOpen] = useState(false);
  const [isJobBoss2DialogOpen, setIsJobBoss2DialogOpen] = useState(false);
  const [isOdysseyDialogOpen, setIsOdysseyDialogOpen] = useState(false);
  const [isEniteoDialogOpen, setIsEniteoDialogOpen] = useState(false);

  const {
    fetchConnectionsByApp,
    getConnectionsByApp,
    generateConnectionToken,
    finalizeConnection,
    connectionStates,
  } = useConnectionsStore();

  const { fetchApps, getAppById } = useAgentWorkflowAppsStore();

  const app = getAppById(appId);
  const connections = getConnectionsByApp(appId);
  const filteredConnections = connections.filter(
    (connection) =>
      connection.connectionProviderId === providerId &&
      !disabledConnectionIds.includes(connection.id),
  );
  const connectionState = connectionStates.get(appId) || {
    isLoadingGet: false,
    isLoadingCreate: false,
    isLoadingFinalize: false,
    getError: null,
    createError: null,
    finalizeError: null,
  };

  useEffect(() => {
    if (!app) {
      fetchApps();
    }
  }, [app, fetchApps]);

  useEffect(() => {
    fetchConnectionsByApp(appId);
  }, [appId, fetchConnectionsByApp]);

  useEffect(() => {
    setError(null);
    setIsDynamicsDialogOpen(false);
    setIsEpicorDialogOpen(false);
    setIsProphet21DialogOpen(false);
    setIsJobBoss2DialogOpen(false);
    setIsEniteoDialogOpen(false);
    setIsConnecting(false);
  }, [providerId, appId]);

  // Listen for the OAuth callback tab finalizing a connection, then refresh and
  // select it in this tab.
  useEffect(() => {
    return subscribeToOAuthConnections(async (message) => {
      if (message.providerId !== providerId) {
        return;
      }
      await fetchConnectionsByApp(appId);
      onSelect(message.connectionId);
      setError(null);
      setIsConnecting(false);
    });
  }, [providerId, appId, fetchConnectionsByApp, onSelect]);

  // This is a hack for pipedream to allow the iframe to be clickable if a shadcn dialog is open
  useEffect(() => {
    if (isPipedreamActive) {
      // Create a style element to override Sheet's z-index and pointer-events
      const style = document.createElement("style");
      style.id = "pipedream-iframe-override";
      style.textContent = `
        /* Allow Pipedream iframe to be clickable over Sheet */
        [data-radix-dialog-content] {
          pointer-events: none !important;
        }
        [data-radix-dialog-content] > * {
          pointer-events: none !important;
        }
        /* Ensure the Pipedream iframe remains clickable */
        iframe#pipedream-connect-iframe-0,
        iframe[id^="pipedream-connect-iframe"] {
          pointer-events: auto !important;
        }
      `;
      document.head.appendChild(style);

      return () => {
        const styleElement = document.getElementById(
          "pipedream-iframe-override",
        );
        if (styleElement) {
          styleElement.remove();
        }
      };
    }
  }, [isPipedreamActive]);

  const handleCancelConnection = useCallback(() => {
    setIsPipedreamActive(false);
    setIsConnecting(false);
    // Remove any Pipedream iframes
    const iframes = document.querySelectorAll(
      'iframe[id^="pipedream-connect-iframe"]',
    );
    iframes.forEach((iframe) => iframe.remove());
  }, []);

  const handleDynamicsConnectionSuccess = useCallback(
    async (newConnectionId: string) => {
      await fetchConnectionsByApp(appId);
      onSelect(newConnectionId);
      setError(null);
      setIsConnecting(false);
      setIsDynamicsDialogOpen(false);
    },
    [appId, fetchConnectionsByApp, onSelect],
  );

  const handleEpicorConnectionSuccess = useCallback(
    async (newConnectionId: string) => {
      await fetchConnectionsByApp(appId);
      onSelect(newConnectionId);
      setError(null);
      setIsConnecting(false);
      setIsEpicorDialogOpen(false);
    },
    [appId, fetchConnectionsByApp, onSelect],
  );

  const handleProphet21ConnectionSuccess = useCallback(
    async (newConnectionId: string) => {
      await fetchConnectionsByApp(appId);
      onSelect(newConnectionId);
      setError(null);
      setIsConnecting(false);
      setIsProphet21DialogOpen(false);
    },
    [appId, fetchConnectionsByApp, onSelect],
  );

  const handleJobBoss2ConnectionSuccess = useCallback(
    async (newConnectionId: string) => {
      await fetchConnectionsByApp(appId);
      onSelect(newConnectionId);
      setError(null);
      setIsConnecting(false);
      setIsJobBoss2DialogOpen(false);
    },
    [appId, fetchConnectionsByApp, onSelect],
  );

  const handleOdysseyConnectionSuccess = useCallback(
    async (newConnectionId: string) => {
      await fetchConnectionsByApp(appId);
      onSelect(newConnectionId);
      setError(null);
      setIsConnecting(false);
      setIsOdysseyDialogOpen(false);
    },
    [appId, fetchConnectionsByApp, onSelect],
  );

  const handleEniteoConnectionSuccess = useCallback(
    async (newConnectionId: string) => {
      await fetchConnectionsByApp(appId);
      onSelect(newConnectionId);
      setError(null);
      setIsConnecting(false);
      setIsEniteoDialogOpen(false);
    },
    [appId, fetchConnectionsByApp, onSelect],
  );

  const handleCreateConnection = useCallback(async () => {
    try {
      setIsConnecting(true);
      setError(null);

      if (providerId === DYNAMICS_PROVIDER_ID) {
        setIsDynamicsDialogOpen(true);
        setIsConnecting(false);
        return;
      }

      if (providerId === EPICOR_PROVIDER_ID) {
        setIsEpicorDialogOpen(true);
        setIsConnecting(false);
        return;
      }

      if (providerId === PROPHET21_PROVIDER_ID) {
        setIsProphet21DialogOpen(true);
        setIsConnecting(false);
        return;
      }

      if (providerId === JOBBOSS2_PROVIDER_ID) {
        setIsJobBoss2DialogOpen(true);
        setIsConnecting(false);
        return;
      }

      if (providerId === ODYSSEY_PROVIDER_ID) {
        setIsOdysseyDialogOpen(true);
        setIsConnecting(false);
        return;
      }

      if (providerId === ENITEO_PROVIDER_ID) {
        setIsEniteoDialogOpen(true);
        setIsConnecting(false);
        return;
      }

      const oauthConfig = Object.values(OAUTH_PROVIDERS).find(
        (c) => c.connectionProviderId === providerId,
      );
      if (oauthConfig) {
        // Open the tab synchronously so popup blockers treat it as a direct
        // result of the click; its URL is set once the token resolves.
        const authWindow = window.open("", "_blank");

        const tokenResponse = await generateConnectionToken(
          providerId,
          oauthConfig.appId,
        );
        if (!tokenResponse?.authorizationUrl) {
          authWindow?.close();
          throw new Error(
            `Failed to generate ${oauthConfig.displayName} authorization URL`,
          );
        }
        if (authWindow) {
          authWindow.location.href = tokenResponse.authorizationUrl;
        } else {
          // Popup was blocked — fall back to navigating the current tab.
          window.location.href = tokenResponse.authorizationUrl;
        }
        return;
      }

      if (providerId !== PIPEDREAM_PROVIDER_ID) {
        setError(`Provider ${providerId} is not supported yet`);
        setIsConnecting(false);
        return;
      }

      // Get current user data
      const userResponse = await fetchApi("users/me", "GET");
      if (!userResponse.ok || !userResponse.data) {
        throw new Error("Failed to get user data");
      }
      const userData = userResponse.data as { user: { id: string } };

      // Generate connection token on the backend
      const tokenResponse = await generateConnectionToken(providerId, appId, {
        redirectUri: window.location.href,
      });
      if (!tokenResponse) {
        throw new Error("Failed to generate connection token");
      }
      const { token, clientId } = tokenResponse;
      if (!token) {
        throw new Error("Failed to generate connection token");
      }

      // Set flag to indicate Pipedream iframe is active
      setIsPipedreamActive(true);

      // Use Pipedream SDK to connect the account
      const pipedreamClient = new PipedreamClient({
        tokenCallback: async () => {
          // This is the token we just generated
          return {
            token: token,
            expiresAt: new Date(Date.now() + 60 * 60 * 1000), // 1 hour from now
            connectLinkUrl: "", // Not needed for SDK flow
            clientId: clientId,
          };
        },
        externalUserId: userData.user.id,
      });

      pipedreamClient.connectAccount({
        app: appId,
        onClose: () => {
          setIsPipedreamActive(false);
        },
        oauthAppId: clientId,
        onSuccess: async (account: { id: string }) => {
          try {
            // Finalize the connection on our backend
            const connection = await finalizeConnection({
              connectionProviderId: providerId,
              authId: account.id,
              appId,
            });

            if (connection) {
              onSelect(connection.id);

              // Refresh connections list
              await fetchConnectionsByApp(appId);
            } else {
              setError("Failed to finalize connection");
            }
          } catch (finalizeError) {
            const errorMessage =
              finalizeError instanceof Error
                ? finalizeError.message
                : "Failed to finalize connection";
            setError(errorMessage);
          } finally {
            setIsConnecting(false);
          }
        },
        onError: (err: Error) => {
          setError(err.message || "Connection failed");
          setIsConnecting(false);
        },
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to create connection";
      setError(errorMessage);
      setIsConnecting(false);
      setIsPipedreamActive(false);
    }
  }, [
    appId,
    providerId,
    generateConnectionToken,
    finalizeConnection,
    onSelect,
    fetchConnectionsByApp,
  ]);

  const selectedConnection = filteredConnections.find(
    (c) => c.id === connectionId,
  );

  const canCreateConnection =
    (providerId !== EPICOR_PROVIDER_ID || epicorPrimitiveSupportEnabled) &&
    (providerId !== PROPHET21_PROVIDER_ID || prophet21SupportEnabled) &&
    (providerId !== JOBBOSS2_PROVIDER_ID || jobBoss2Enabled) &&
    (providerId !== ODYSSEY_PROVIDER_ID || odysseyEnabled) &&
    (providerId !== ENITEO_PROVIDER_ID || eniteoSupportEnabled);

  return (
    <div className="w-full space-y-2 relative">
      {/* Backdrop overlay when Pipedream iframe is active */}
      {isPipedreamActive && (
        <div className="fixed inset-0 bg-black/50 z-[2147483646] pointer-events-none">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-4 shadow-lg">
            <button
              onClick={handleCancelConnection}
              className="absolute top-2 right-2 p-1 rounded-md hover:bg-gray-100 pointer-events-auto"
              aria-label="Cancel connection"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin" />
              <p className="text-sm font-medium">
                Connecting to {app?.name}...
              </p>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Complete the authentication in the popup window
            </p>
          </div>
        </div>
      )}
      <Select
        value={connectionId || undefined}
        onValueChange={onSelect}
        disabled={disabled}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Select a connection">
            {selectedConnection ? (
              <div className="flex items-center gap-2">
                <span>{selectedConnection.name || "Connection"}</span>
                {selectedConnection.status === "ERROR" && (
                  <AlertCircle className="h-3 w-3 text-destructive" />
                )}
              </div>
            ) : (
              "Select a connection"
            )}
          </SelectValue>
        </SelectTrigger>

        <SelectContent>
          {connectionState.isLoadingGet ? (
            <div className="flex items-center justify-center p-3">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : filteredConnections.length === 0 ? (
            <div className="p-3 text-center text-sm text-muted-foreground">
              No connections found
            </div>
          ) : (
            filteredConnections.map((connection) => (
              <SelectItem key={connection.id} value={connection.id}>
                <div className="flex items-center gap-2">
                  <span>
                    {connection.name ||
                      `Connection ${connection.id.slice(0, 8)}`}
                  </span>
                  {connection.status === "ERROR" && (
                    <AlertCircle className="h-3 w-3 text-destructive" />
                  )}
                  {connection.status === "PENDING" && (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  )}
                </div>
              </SelectItem>
            ))
          )}

          {canCreateConnection && (
            <>
              <SelectSeparator />

              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCreateConnection();
                }}
                disabled={
                  disabled || isConnecting || connectionState.isLoadingFinalize
                }
              >
                Create Connection
                {isConnecting || connectionState.isLoadingFinalize ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="mr-2 h-4 w-4" />
                )}
              </Button>
            </>
          )}
        </SelectContent>
      </Select>

      {(error || connectionState.getError || connectionState.finalizeError) && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error || connectionState.getError || connectionState.finalizeError}
          </AlertDescription>
        </Alert>
      )}

      {providerId === DYNAMICS_PROVIDER_ID && (
        <DynamicsConnectionDialog
          open={isDynamicsDialogOpen}
          onOpenChange={(open) => {
            setIsDynamicsDialogOpen(open);
            if (!open) {
              setIsConnecting(false);
              setError(null);
            }
          }}
          onSuccess={handleDynamicsConnectionSuccess}
        />
      )}
      {epicorPrimitiveSupportEnabled && providerId === EPICOR_PROVIDER_ID && (
        <EpicorConnectionDialog
          open={isEpicorDialogOpen}
          onOpenChange={(open) => {
            setIsEpicorDialogOpen(open);
            if (!open) {
              setIsConnecting(false);
              setError(null);
            }
          }}
          onSuccess={handleEpicorConnectionSuccess}
        />
      )}
      {prophet21SupportEnabled && providerId === PROPHET21_PROVIDER_ID && (
        <Prophet21ConnectionDialog
          open={isProphet21DialogOpen}
          onOpenChange={(open) => {
            setIsProphet21DialogOpen(open);
            if (!open) {
              setIsConnecting(false);
              setError(null);
            }
          }}
          onSuccess={handleProphet21ConnectionSuccess}
        />
      )}
      {jobBoss2Enabled && providerId === JOBBOSS2_PROVIDER_ID && (
        <JobBoss2ConnectionDialog
          open={isJobBoss2DialogOpen}
          onOpenChange={(open) => {
            setIsJobBoss2DialogOpen(open);
            if (!open) {
              setIsConnecting(false);
              setError(null);
            }
          }}
          onSuccess={handleJobBoss2ConnectionSuccess}
        />
      )}
      {odysseyEnabled && providerId === ODYSSEY_PROVIDER_ID && (
        <OdysseyConnectionDialog
          open={isOdysseyDialogOpen}
          onOpenChange={(open) => {
            setIsOdysseyDialogOpen(open);
            if (!open) {
              setIsConnecting(false);
              setError(null);
            }
          }}
          onSuccess={handleOdysseyConnectionSuccess}
        />
      )}
      {eniteoSupportEnabled && providerId === ENITEO_PROVIDER_ID && (
        <EniteoConnectionDialog
          open={isEniteoDialogOpen}
          onOpenChange={(open) => {
            setIsEniteoDialogOpen(open);
            if (!open) {
              setIsConnecting(false);
              setError(null);
            }
          }}
          onSuccess={handleEniteoConnectionSuccess}
        />
      )}
    </div>
  );
}
